import { useNavigation } from '@react-navigation/native';
import { styled } from 'dripsy';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';

import { BaseText, Touchable, TouchableTypeEnum } from '@components';
import { screens, TabNavigatorNavigationPropType } from '@navigation';
import { useViewerModel } from '@models/Viewer/ViewerModel';

const UserStatisticContainer = styled(View)({
  flexDirection: 'row',
  justifyContent: 'space-around',
  alignItems: 'center',
  bg: '$background10',
  borderRadius: 12,
  height: 75,
  px: '$8',
  width: ['100%', 350],
});

const TouchableStatisticContainer = styled(Touchable)({
  width: 100,
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
});

interface ITextStyleProps {
  lighter?: boolean;
}

const StatisticText = styled(BaseText)((props: ITextStyleProps) => ({
  px: '$4',
  color: props.lighter ? '$text80' : '$text400',
  variant: 'text.small',
}));

const Title = styled(BaseText)((props: ITextStyleProps) => ({
  textAlign: ['center', 'left'],
  fontWeight: 'semi',
  variant: 'text.large',
  color: props.lighter ? '$text300' : '$heading800',
}));

type NavigationPropType = TabNavigatorNavigationPropType<'Profile'>;

export const UserStatsView = observer(() => {
  const nav = useNavigation<NavigationPropType>();
  const { t } = useTranslation();
  const viewer = useViewerModel();

  const user = viewer.currentUser;
  const hasFollowers = !!user?.shared.followers;
  const hasFollowing = !!user?.shared.following;

  function navigateToFollowers() {
    if (user) {
      nav.push(screens.Followers, {
        id: user.id,
      });
    }
  }

  function navigateToFollowing() {
    if (user) {
      nav.push(screens.Following, {
        id: user.id,
      });
    }
  }

  return (
    <UserStatisticContainer>
      <TouchableStatisticContainer
        disabled={!hasFollowers}
        type={TouchableTypeEnum.Opacity}
        onPress={navigateToFollowers}
      >
        <StatisticText>{t('profile.followers')}</StatisticText>
        <Title lighter={!hasFollowers}>
          {user?.shared?.followers}
        </Title>
      </TouchableStatisticContainer>

      <TouchableStatisticContainer
        disabled={!hasFollowing}
        type={TouchableTypeEnum.Opacity}
        onPress={navigateToFollowing}
      >
        <StatisticText>{t('profile.following')}</StatisticText>
        <Title lighter={!hasFollowing}>
          {user?.shared?.following}
        </Title>
      </TouchableStatisticContainer>
    </UserStatisticContainer>
  );
});
